import {
  METHOD_LABEL,
  type Bill,
  type PaymentMethod,
} from '@/features/billing/types';

/** RL-2194: how a bill was paid: panel part and/or the patient's method(s). */
export type PaidByFilter = 'all' | 'panel' | PaymentMethod;

export const PAID_BY_OPTIONS: { value: PaidByFilter; label: string }[] = [
  { value: 'all', label: 'All payment methods' },
  { value: 'panel', label: 'Panel' },
  { value: 'cash', label: METHOD_LABEL.cash },
  { value: 'duitnow_qr', label: METHOD_LABEL.duitnow_qr },
  { value: 'card', label: METHOD_LABEL.card },
  { value: 'e_wallet', label: METHOD_LABEL.e_wallet },
  { value: 'deposit', label: METHOD_LABEL.deposit },
];

/** Distinct methods the patient paid with, in the order first used. */
export function patientMethods(b: Bill): PaymentMethod[] {
  return [
    ...new Set(b.payments.filter((p) => p.amountSen > 0).map((p) => p.method)),
  ];
}

/** The panel is a payer only when it carries part of the bill. */
function panelName(b: Bill): string | null {
  return b.panelSen > 0 ? (b.payer?.name ?? 'Panel') : null;
}

export function billPaymentSummary(b: Bill): string {
  if (b.status === 'void') return 'Void';
  if (b.status === 'open') return b.paidSen > 0 ? 'Part paid' : 'Not paid';
  const parts: string[] = [];
  const panel = panelName(b);
  if (panel) parts.push(`Panel ${panel}`);
  const methods = patientMethods(b);
  if (methods.length)
    parts.push(methods.map((m) => METHOD_LABEL[m]).join(' + '));
  if (parts.length === 0) parts.push('No payment due');
  if (b.refundedSen > 0) parts.push('refunded');
  return parts.join(' + ');
}

export function billMatchesPaidBy(b: Bill, filter: PaidByFilter): boolean {
  if (filter === 'all') return true;
  if (b.status !== 'closed') return false;
  if (filter === 'panel') return panelName(b) !== null;
  return patientMethods(b).includes(filter);
}
